/**
 * Admin Classes Page
 *
 * Page for managing class groups (Director only).
 * Allows creating, editing, and deleting class groups.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useAdminClassGroups,
  useAdminLevels,
  useAdminTeachers,
  useCreateClassGroup,
  useDeleteClassGroup,
  useHardDeleteClassGroup,
  useReactivateClassGroup,
  useUpdateClassGroup,
} from "../../hooks/useAdmin";
import type {
  AdminClassGroup,
  CreateClassGroupRequest,
  UpdateClassGroupRequest,
} from "../../types/admin";

const DAYS_OPTIONS = [
  { value: "Mon", label: "Monday" },
  { value: "Tue", label: "Tuesday" },
  { value: "Wed", label: "Wednesday" },
  { value: "Thu", label: "Thursday" },
  { value: "Fri", label: "Friday" },
  { value: "Sat", label: "Saturday" },
];

const CLASS_TYPE_OPTIONS = [
  { value: "individual", label: "Individual", max: 1 },
  { value: "duo", label: "Duo", max: 2 },
  { value: "group", label: "Grupo", max: 4 },
];

// Helper to format schedule for display
function formatScheduleDisplay(schedule: any): string {
  if (!schedule) return "No schedule";

  const dayMap: Record<string, string> = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    Mon: "Mon",
    Tue: "Tue",
    Wed: "Wed",
    Thu: "Thu",
    Fri: "Fri",
    Sat: "Sat",
  };

  // New format: array of day schedules
  if (Array.isArray(schedule)) {
    if (schedule.length === 0) return "No schedule";

    // Check if all times are the same
    const times = schedule.map((s: any) => s.time);
    const allSameTime = times.every((t: string) => t === times[0]);

    if (allSameTime) {
      // All same time: "Mon, Wed at 10:00"
      const days = schedule
        .map((s: any) => dayMap[s.day.toLowerCase()] || s.day)
        .join(", ");
      return `${days} at ${times[0]}`;
    } else {
      // Different times: "Mon 10:00, Wed 14:00"
      return schedule
        .map((s: any) => {
          const day = dayMap[s.day.toLowerCase()] || s.day;
          return `${day} ${s.time}`;
        })
        .join(", ");
    }
  }

  // Legacy format: object with days and time
  if (schedule.days && schedule.time) {
    const days = schedule.days
      .map((d: string) => dayMap[d] || d)
      .join(", ");
    return `${days} at ${schedule.time}`;
  }

  return "No schedule";
}

interface DaySchedule {
  day: string;
  time: string;
}

interface ClassFormData {
  name: string;
  levelCode: string;
  classType: "individual" | "duo" | "group";
  maxStudents: number;
  teacherId: string;
  daySchedules: DaySchedule[];
  meetLink: string;
  whiteboardUrl: string;
}

const initialFormData: ClassFormData = {
  name: "",
  levelCode: "A1",
  classType: "individual",
  maxStudents: 1,
  teacherId: "",
  daySchedules: [],
  meetLink: "",
  whiteboardUrl: "",
};

export function AdminClassesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const levelFilter = searchParams.get("level") || "";
  const statusFilter = searchParams.get("status") || "active";
  const action = searchParams.get("action");

  const [selectedClass, setSelectedClass] = useState<AdminClassGroup | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ClassFormData>(initialFormData);
  const [actionModal, setActionModal] = useState<{
    type: "deactivate" | "reactivate" | "delete";
    classGroup: AdminClassGroup;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyEmpty, setOnlyEmpty] = useState(false);

  const { data: classes, isLoading } = useAdminClassGroups({
    level: levelFilter || undefined,
    isActive:
      statusFilter === "inactive"
        ? false
        : statusFilter === "active"
          ? true
          : undefined,
  });
  const { data: levels } = useAdminLevels();
  const { data: teachers } = useAdminTeachers();

  const createClass = useCreateClassGroup();
  const updateClass = useUpdateClassGroup();
  const deleteClass = useDeleteClassGroup();
  const reactivateClass = useReactivateClassGroup();
  const hardDeleteClass = useHardDeleteClassGroup();

  // Search (class name OR active student name) + "no students" quick filter.
  const filteredClasses = (classes ?? []).filter((c) => {
    if (onlyEmpty && c.studentCount !== 0) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const inName = c.name.toLowerCase().includes(q);
      const inStudent = (c.students ?? []).some((s) =>
        s.fullName.toLowerCase().includes(q),
      );
      if (!inName && !inStudent) return false;
    }
    return true;
  });

  // Open create modal if action=create
  useEffect(() => {
    if (action === "create") {
      handleOpenCreate();
      // Clear the action param
      setSearchParams((prev) => {
        prev.delete("action");
        return prev;
      });
    }
  }, [action]);

  const handleOpenCreate = () => {
    setFormData(initialFormData);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (classGroup: AdminClassGroup) => {
    // Convert schedule to daySchedules format
    let daySchedules: DaySchedule[] = [];

    if (classGroup.schedule) {
      // Check if it's array format (new) or object format (legacy)
      if (Array.isArray(classGroup.schedule)) {
        daySchedules = classGroup.schedule.map((s: any) => ({
          day: s.day.charAt(0).toUpperCase() + s.day.slice(1, 3),
          time: s.time,
        }));
      } else if (classGroup.schedule.days && classGroup.schedule.time) {
        // Legacy format - same time for all days
        daySchedules = classGroup.schedule.days.map((day: string) => ({
          day,
          time: classGroup.schedule!.time!,
        }));
      }
    }

    setFormData({
      name: classGroup.name,
      levelCode: classGroup.levelCode,
      classType: classGroup.classType,
      maxStudents: classGroup.maxStudents,
      teacherId: classGroup.teacherId || "",
      daySchedules,
      meetLink: classGroup.meetLink || "",
      whiteboardUrl: classGroup.whiteboardUrl || "",
    });
    setSelectedClass(classGroup);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClass(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Convert daySchedules to API format
    const schedule =
      formData.daySchedules.length > 0
        ? {
            days: formData.daySchedules.map((ds) => ds.day),
            time: formData.daySchedules[0]?.time, // Use first day's time as default
            durationMinutes: 60,
          }
        : undefined;

    if (isEditing && selectedClass) {
      const data: UpdateClassGroupRequest = {
        name: formData.name,
        levelCode: formData.levelCode,
        classType: formData.classType,
        maxStudents: formData.maxStudents,
        teacherId: formData.teacherId || null,
        schedule,
        meetLink: formData.meetLink || undefined,
        whiteboardUrl: formData.whiteboardUrl || undefined,
      };

      await updateClass.mutateAsync({ id: selectedClass.id, data });
    } else {
      const data: CreateClassGroupRequest = {
        name: formData.name,
        levelCode: formData.levelCode,
        classType: formData.classType,
        maxStudents: formData.maxStudents,
        teacherId: formData.teacherId || undefined,
        schedule,
        meetLink: formData.meetLink || undefined,
        whiteboardUrl: formData.whiteboardUrl || undefined,
      };

      await createClass.mutateAsync(data);
    }

    handleCloseModal();
  };

  const handleAction = async (
    type: "deactivate" | "reactivate" | "delete",
    classId: string,
  ) => {
    if (type === "deactivate") {
      await deleteClass.mutateAsync(classId);
    } else if (type === "reactivate") {
      await reactivateClass.mutateAsync(classId);
    } else if (type === "delete") {
      await hardDeleteClass.mutateAsync(classId);
    }
    setActionModal(null);
  };

  const handleStatusFilter = (status: string) => {
    setSearchParams((prev) => {
      if (status === "active") {
        prev.delete("status");
      } else {
        prev.set("status", status);
      }
      return prev;
    });
  };

  const handleLevelFilter = (level: string) => {
    setSearchParams((prev) => {
      if (level) {
        prev.set("level", level);
      } else {
        prev.delete("level");
      }
      return prev;
    });
  };

  const handleClassTypeChange = (type: "individual" | "duo" | "group") => {
    const maxStudents =
      CLASS_TYPE_OPTIONS.find((o) => o.value === type)?.max || 1;
    setFormData((prev) => ({
      ...prev,
      classType: type,
      maxStudents,
    }));
  };

  const toggleDay = (day: string) => {
    setFormData((prev) => {
      const exists = prev.daySchedules.some((ds) => ds.day === day);
      if (exists) {
        // Remove this day
        return {
          ...prev,
          daySchedules: prev.daySchedules.filter((ds) => ds.day !== day),
        };
      } else {
        // Add this day with default time
        return {
          ...prev,
          daySchedules: [...prev.daySchedules, { day, time: "19:00" }],
        };
      }
    });
  };

  const updateDayTime = (day: string, time: string) => {
    setFormData((prev) => ({
      ...prev,
      daySchedules: prev.daySchedules.map((ds) =>
        ds.day === day ? { ...ds, time } : ds
      ),
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Manage Classes
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Create and manage classes, schedules and links
              </p>
            </div>
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-add-line"></i>
              New Class
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          {/* Search (left) */}
          <div className="relative flex-1 min-w-[180px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-earth-400"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by class or student name..."
              className="w-full pl-10 pr-4 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 bg-white"
            />
          </div>

          {/* Level Filter (dropdown, right) */}
          <select
            value={levelFilter}
            onChange={(e) => handleLevelFilter(e.target.value)}
            className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 bg-white"
          >
            <option value="">All levels</option>
            {levels?.map((level) => (
              <option key={level.code} value={level.code}>
                {level.code}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 bg-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>

          {/* No-students quick filter */}
          <button
            onClick={() => setOnlyEmpty((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              onlyEmpty
                ? "bg-amber-500 text-white"
                : "bg-white border border-earth-200 text-earth-600 hover:bg-earth-50"
            }`}
          >
            <i className="ri-user-unfollow-line mr-1"></i>
            No students
          </button>
        </div>

        {/* Classes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClasses.map((classGroup) => {
            const isEmpty = classGroup.isActive && classGroup.studentCount === 0;
            return (
            <div
              key={classGroup.id}
              className={`bg-white rounded-xl border p-4 hover:border-earth-300 transition-colors ${
                !classGroup.isActive
                  ? "border-red-200 bg-red-50/30"
                  : isEmpty
                    ? "border-amber-300 bg-amber-50/40"
                    : "border-earth-200"
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-earth-800 truncate">
                      {classGroup.name}
                    </h3>
                    {!classGroup.isActive && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span className="px-2 py-0.5 bg-earth-100 text-earth-700 rounded text-xs font-medium">
                      {classGroup.levelCode}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        classGroup.classType === "individual"
                          ? "bg-blue-100 text-blue-700"
                          : classGroup.classType === "duo"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {CLASS_TYPE_OPTIONS.find(
                        (o) => o.value === classGroup.classType,
                      )?.label || classGroup.classType}
                    </span>
                    {isEmpty && (
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">
                        No students
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleOpenEdit(classGroup)}
                    className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <i className="ri-edit-line text-earth-500"></i>
                  </button>
                  {classGroup.isActive ? (
                    <>
                      {isEmpty && (
                        <button
                          onClick={() =>
                            setActionModal({ type: "delete", classGroup })
                          }
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete (no students)"
                        >
                          <i className="ri-delete-bin-line text-red-500"></i>
                        </button>
                      )}
                      <button
                        onClick={() =>
                          setActionModal({ type: "deactivate", classGroup })
                        }
                        className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Deactivate"
                      >
                        <i className="ri-pause-circle-line text-amber-500"></i>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() =>
                          setActionModal({ type: "reactivate", classGroup })
                        }
                        className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                        title="Reactivate"
                      >
                        <i className="ri-play-circle-line text-green-500"></i>
                      </button>
                      <button
                        onClick={() =>
                          setActionModal({ type: "delete", classGroup })
                        }
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete permanently"
                      >
                        <i className="ri-delete-bin-line text-red-500"></i>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="space-y-2 text-sm">
                {/* Teacher */}
                <div className="flex items-center gap-2 text-earth-600">
                  <i className="ri-user-star-line text-earth-400"></i>
                  <span>{classGroup.teacherName || "No teacher"}</span>
                </div>

                {/* Schedule */}
                <div className="flex items-center gap-2 text-earth-600">
                  <i className="ri-calendar-line text-earth-400"></i>
                  <span>{formatScheduleDisplay(classGroup.schedule)}</span>
                </div>

                {/* Students */}
                <div className="flex items-center gap-2 text-earth-600">
                  <i className="ri-user-3-line text-earth-400"></i>
                  <span>
                    {classGroup.studentCount}/{classGroup.maxStudents} alunos
                  </span>
                </div>

                {/* Price */}
                {classGroup.pricePerMonth != null && (
                  <div className="flex items-center gap-2 text-earth-600">
                    <i className="ri-money-dollar-circle-line text-earth-400"></i>
                    <span>R$ {Number(classGroup.pricePerMonth).toFixed(2)}/mês</span>
                  </div>
                )}

                {/* Links */}
                <div className="flex items-center gap-3 pt-2 border-t border-earth-100">
                  {classGroup.meetLink ? (
                    <a
                      href={classGroup.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                    >
                      <i className="ri-video-chat-line"></i>
                      <span className="text-xs">Meet</span>
                    </a>
                  ) : (
                    <span className="flex items-center gap-1 text-earth-400">
                      <i className="ri-video-chat-line"></i>
                      <span className="text-xs">Sem Meet</span>
                    </span>
                  )}
                  {classGroup.whiteboardUrl ? (
                    <a
                      href={classGroup.whiteboardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-700"
                    >
                      <i className="ri-pencil-ruler-2-line"></i>
                      <span className="text-xs">Whiteboard</span>
                    </a>
                  ) : (
                    <span className="flex items-center gap-1 text-earth-400">
                      <i className="ri-pencil-ruler-2-line"></i>
                      <span className="text-xs">Sem Whiteboard</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Students Preview */}
              {classGroup.students.length > 0 && (
                <div className="mt-3 pt-3 border-t border-earth-100">
                  <p className="text-xs text-earth-700 mb-2">Alunos:</p>
                  <div className="flex flex-wrap gap-1">
                    {classGroup.students.map((student) => (
                      <span
                        key={student.id}
                        className="px-2 py-0.5 bg-earth-100 text-earth-700 rounded text-xs"
                      >
                        {student.fullName.split(" ")[0]}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })}

          {/* Empty State */}
          {filteredClasses.length === 0 && (
            <div className="col-span-full text-center py-12 bg-white rounded-xl border border-earth-200">
              <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-2xl text-earth-400"></i>
              </div>
              <p className="text-earth-600 font-medium">No classes found</p>
              <p className="text-earth-700 text-sm mt-1">
                {searchQuery || onlyEmpty
                  ? "Try a different search or filter"
                  : "Create a new class to get started"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={handleCloseModal}
          ></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Class" : "New Class"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Class Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Ex: A1 - John - Tuesday - 7pm"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  required
                />
              </div>

              {/* Level & Class Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Nível
                  </label>
                  <select
                    value={formData.levelCode}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        levelCode: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  >
                    {levels?.map((level) => (
                      <option key={level.code} value={level.code}>
                        {level.code} - {level.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={formData.classType}
                    onChange={(e) =>
                      handleClassTypeChange(
                        e.target.value as "individual" | "duo" | "group",
                      )
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  >
                    {CLASS_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} (até {option.max})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Teacher */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Teacher
                </label>
                <select
                  value={formData.teacherId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      teacherId: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                >
                  <option value="">No teacher</option>
                  {teachers
                    ?.filter((t) => t.isActive)
                    .map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName}
                      </option>
                    ))}
                </select>
              </div>

              {/* Schedule - Days */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">
                  Days of the Week
                </label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OPTIONS.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        formData.daySchedules.some((ds) => ds.day === day.value)
                          ? "bg-olive-600 text-white"
                          : "bg-earth-100 text-earth-600 hover:bg-earth-200"
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule - Times per Day */}
              {formData.daySchedules.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-2">
                    Times for Each Day
                  </label>
                  <div className="space-y-2">
                    {formData.daySchedules
                      .sort((a, b) => {
                        const orderMap: Record<string, number> = {
                          Mon: 0,
                          Tue: 1,
                          Wed: 2,
                          Thu: 3,
                          Fri: 4,
                          Sat: 5,
                        };
                        return orderMap[a.day] - orderMap[b.day];
                      })
                      .map((ds) => {
                        const dayLabel =
                          DAYS_OPTIONS.find((d) => d.value === ds.day)?.label ||
                          ds.day;
                        return (
                          <div
                            key={ds.day}
                            className="flex items-center gap-3 p-3 bg-earth-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <span className="text-sm font-medium text-earth-700">
                                {dayLabel}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <i className="ri-time-line text-earth-400"></i>
                              <input
                                type="time"
                                value={ds.time}
                                onChange={(e) =>
                                  updateDayTime(ds.day, e.target.value)
                                }
                                className="px-3 py-1.5 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 bg-white"
                                required
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="space-y-3 pt-3 border-t border-earth-100">
                <h3 className="text-sm font-medium text-earth-700">Links</h3>

                <div>
                  <label className="block text-xs text-earth-700 mb-1">
                    Google Meet
                  </label>
                  <input
                    type="url"
                    value={formData.meetLink}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        meetLink: e.target.value,
                      }))
                    }
                    placeholder="https://meet.google.com/xxx-xxxx-xxx"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  />
                </div>

                <div>
                  <label className="block text-xs text-earth-700 mb-1">
                    Whiteboard (Canva)
                  </label>
                  <input
                    type="url"
                    value={formData.whiteboardUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        whiteboardUrl: e.target.value,
                      }))
                    }
                    placeholder="https://www.canva.com/design/..."
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createClass.isPending || updateClass.isPending}
                  className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {createClass.isPending || updateClass.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size="sm" />
                      Saving...
                    </span>
                  ) : isEditing ? (
                    "Save Changes"
                  ) : (
                    "Create Class"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setActionModal(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6">
            <div className="text-center">
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  actionModal.type === "delete"
                    ? "bg-red-100"
                    : actionModal.type === "deactivate"
                      ? "bg-amber-100"
                      : "bg-green-100"
                }`}
              >
                <i
                  className={`text-xl ${
                    actionModal.type === "delete"
                      ? "ri-delete-bin-line text-red-600"
                      : actionModal.type === "deactivate"
                        ? "ri-pause-circle-line text-amber-600"
                        : "ri-play-circle-line text-green-600"
                  }`}
                ></i>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-earth-800 mb-2">
                {actionModal.type === "delete"
                  ? "Delete Class Permanently?"
                  : actionModal.type === "deactivate"
                    ? "Deactivate Class?"
                    : "Reactivate Class?"}
              </h3>

              {/* Description */}
              <p className="text-sm text-earth-700 mb-2">
                <strong>{actionModal.classGroup.name}</strong>
              </p>
              <p className="text-sm text-earth-700 mb-6">
                {actionModal.type === "delete"
                  ? "This action cannot be undone. The class and all associated data will be permanently removed."
                  : actionModal.type === "deactivate"
                    ? "The class will be deactivated. Students will not be removed automatically."
                    : "The class will be reactivated and become available again."}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    handleAction(actionModal.type, actionModal.classGroup.id)
                  }
                  disabled={
                    deleteClass.isPending ||
                    reactivateClass.isPending ||
                    hardDeleteClass.isPending
                  }
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                    actionModal.type === "delete"
                      ? "bg-red-600 hover:bg-red-700"
                      : actionModal.type === "deactivate"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {deleteClass.isPending ||
                  reactivateClass.isPending ||
                  hardDeleteClass.isPending
                    ? "Processing..."
                    : actionModal.type === "delete"
                      ? "Delete Permanently"
                      : actionModal.type === "deactivate"
                        ? "Deactivate"
                        : "Reactivate"}
                </button>
                <button
                  onClick={() => setActionModal(null)}
                  className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminClassesPage;
